import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { PageHero, Overline } from "../components/blocks";
import { Reveal } from "../components/motion/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { IMAGES } from "../content/defaultContent";

const empty = { firstName: "", lastName: "", company: "", email: "", subject: "", message: "" };

export default function Contact() {
  const { content } = useLanguage();
  const ct = content.contact;
  const f = content.ui.form;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/contact", {
        ...form,
        subject: form.subject || ct.subjects[0],
      });
      toast.success(f.success);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || f.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-none border-b border-brand-grey bg-transparent py-4 text-brand-black placeholder:text-brand-black/40 focus:border-brand-green focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero overline={ct.hero.overline} title={ct.hero.title} subtitle={ct.hero.subtitle} image={IMAGES.b2b} />

      <section className="bg-brand-beige py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-12">
          {/* info */}
          <div className="md:col-span-4">
            <Overline>Coordonnées</Overline>
            <div className="mt-8 space-y-8">
              <div className="flex items-start gap-4">
                <MapPin className="mt-1 h-5 w-5 text-brand-gold" />
                <p data-testid="contact-address" className="text-lg text-brand-black/80">{ct.address}</p>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="mt-1 h-5 w-5 text-brand-gold" />
                <a href={`tel:${ct.phone}`} data-testid="contact-phone" className="text-lg text-brand-black/80 hover:text-brand-green">{ct.phone}</a>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="mt-1 h-5 w-5 text-brand-gold" />
                <a href={`mailto:${ct.email}`} data-testid="contact-email" className="text-lg text-brand-black/80 hover:text-brand-green">{ct.email}</a>
              </div>
            </div>
          </div>

          {/* form */}
          <div className="md:col-span-8">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <input data-testid="contact-firstname" required placeholder={f.firstName} value={form.firstName} onChange={set("firstName")} className={inputCls} />
                <input data-testid="contact-lastname" required placeholder={f.lastName} value={form.lastName} onChange={set("lastName")} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <input data-testid="contact-company" placeholder={f.company} value={form.company} onChange={set("company")} className={inputCls} />
                <input data-testid="contact-email-input" type="email" required placeholder={f.email} value={form.email} onChange={set("email")} className={inputCls} />
              </div>

              <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                <SelectTrigger data-testid="contact-subject" className="w-full rounded-none border-0 border-b border-brand-grey bg-transparent py-4 text-brand-black focus:ring-0">
                  <SelectValue placeholder={f.subject} />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {ct.subjects.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`subject-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <textarea data-testid="contact-message" required rows={5} placeholder={f.message} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="rounded-none bg-brand-green px-12 py-5 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-brand-black disabled:opacity-50"
              >
                {loading ? content.ui.buttons.sending : content.ui.buttons.send}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-white" data-testid="contact-map">
        <div className="h-[420px] w-full grayscale">
          <iframe
            title="Google Maps - Congo Brazzaville"
            src={`https://www.google.com/maps?q=${encodeURIComponent(ct.mapQuery)}&output=embed`}
            className="h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}
